/**
 * Revantage Europe customer module (frontend).
 *
 * Composes the Rev EU product module views and returns them with the routes
 * they serve under the shell's root layout. The shell registers this module in
 * one place (`createCompositionRootUI`); nothing else in the shell imports it.
 * The backend twin is `src/proda_finance/modules/rev_eu`.
 */
import type { RouteObject } from "react-router-dom";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import { wireBusinessPartners } from "./partners/wireBusinessPartners";
import { wireDealDetail } from "./partners/wireDealDetail";
import { wireDeals } from "./partners/wireDeals";
import { wireSettingsBooks } from "./partners/wireSettingsBooks";
import { wireSettingsChartOfAccounts } from "./master_data/wireSettingsChartOfAccounts";
import { wireSettingsPropertyCodes } from "./master_data/wireSettingsPropertyCodes";
import { wireSettingsSegmentValues } from "./master_data/wireSettingsSegmentValues";
import { wireSettingsYardiImport } from "./master_data/wireSettingsYardiImport";
import { wireServiceProviderCoAMappings } from "./mappings/wireServiceProviderCoAMappings";
import { wireUpload } from "./ingestion/wireUpload";
import { wireFileReview } from "./review/wireFileReview";
import { wireGLLines } from "./review/wireGLLines";
import { wireFiles } from "./submissions/wireFiles";
import { wireHomepage } from "./submissions/wireHomepage";
import { wireSettingsCompany } from "./delivery/wireSettingsCompany";

export const REV_EU_SLUG = "rev_eu";

export const wireRevEU = (deps: TCompositionDeps) => {
    const { ViewHomepage } = wireHomepage(deps);
    const { ViewFiles, ViewFilesTBGL, ViewFilesFinJournals, ViewFilesArchive } = wireFiles(deps);
    const {
        ViewUploadFinJournals,
        ViewUploadStandaloneFinJournals,
        ViewUploadTBGL,
        ViewUploadSupporting,
    } = wireUpload(deps);
    const { ViewDeals, ViewDealCreate } = wireDeals(deps);
    const { ViewServiceProviders, ViewBusinessPartnerDeals, ViewBusinessPartnerDetails } =
        wireBusinessPartners(deps);
    const { ViewFileReview } = wireFileReview(deps);
    const { GLLinesRedirect } = wireGLLines(deps);
    const { ViewChartOfAccounts } = wireSettingsChartOfAccounts(deps);
    const { ViewPropertyCodes } = wireSettingsPropertyCodes(deps);
    const { ViewBooks } = wireSettingsBooks(deps);
    const { ViewSegmentValues } = wireSettingsSegmentValues(deps);
    const { ViewCompanySettings } = wireSettingsCompany(deps);
    const { ViewYardiImport } = wireSettingsYardiImport(deps);
    const { ViewDealDetail } = wireDealDetail(deps);
    const { ViewServiceProviderCoAMappings } = wireServiceProviderCoAMappings(deps);

    // Children of the shell's "/" route. The API gates every one of these by
    // customer module, and the sidebar hides them through permissions.
    const routes: RouteObject[] = [
        { path: "home", element: <ViewHomepage /> },
        { path: "upload/fin-journals-standalone", element: <ViewUploadStandaloneFinJournals /> },
        { path: "upload/tb-gl", element: <ViewUploadTBGL /> },
        { path: "upload/supporting", element: <ViewUploadSupporting /> },
        // The legacy Files and Financial TB/GL lists stay until the Homepage
        // v2 board is enabled everywhere (the `homepage_v2_enabled` company
        // flag). They are what a company without the flag lands on; they are
        // removed once the board is on in production.
        { path: "files", element: <ViewFiles /> },
        { path: "files/tb-gl", element: <ViewFilesTBGL /> },
        { path: "files/fin-journals", element: <ViewFilesFinJournals /> },
        { path: "files/archive", element: <ViewFilesArchive /> },
        { path: "files/:fileId", element: <ViewFileReview /> },
        { path: "files/:fileId/gl-lines", element: <GLLinesRedirect /> },
        { path: "admin/business-partners", element: <ViewServiceProviders /> },
        {
            path: "admin/business-partners/:businessPartnerId/details",
            element: <ViewBusinessPartnerDetails />,
        },
        {
            path: "admin/business-partners/:serviceProviderId/coa",
            element: <ViewServiceProviderCoAMappings />,
        },
        { path: "admin/business-partners/:bpId/deals", element: <ViewBusinessPartnerDeals /> },
        { path: "admin/business-partners/:bpId/deals/new", element: <ViewDealCreate /> },
        { path: "admin/business-partners/:bpId/deals/:dealId", element: <ViewDealDetail /> },
        { path: "admin/portfolio-companies/:pcId/deals/new", element: <ViewDealCreate /> },
        { path: "settings/chart-of-accounts", element: <ViewChartOfAccounts /> },
        { path: "settings/books", element: <ViewBooks /> },
        { path: "admin/submissions", element: <ViewCompanySettings /> },
        { path: "settings/propcodes", element: <ViewPropertyCodes /> },
        { path: "settings/segments", element: <ViewSegmentValues /> },
        { path: "settings/yardi-import", element: <ViewYardiImport /> },
    ];

    return {
        routes,
        ViewFiles,
        ViewFilesTBGL,
        ViewUploadFinJournals,
        ViewUploadTBGL,
        ViewFilesFinJournals,
        ViewFilesArchive,
        ViewFileReview,
        ViewDeals,
        ViewDealCreate,
        ViewServiceProviders,
        ViewBusinessPartnerDeals,
        ViewDealDetail,
        ViewChartOfAccounts,
        ViewPropertyCodes,
        ViewSegmentValues,
        ViewCompanySettings,
        ViewYardiImport,
    };
};
